import { Component, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { Subscription } from "rxjs";
import { NAVIGATIONS } from "src/app/shared/consts/navigations";
import { StatisticsService } from "./services/statistics.service";
import { environment } from "src/environments/environment";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";

declare var $: any;
@Component({
  selector: "app-statistics",
  templateUrl: "statistics.component.html",
  styleUrls: ["./statistics.component.css"],
})
export class StatisticsComponent implements OnInit, OnDestroy {

  readonly NAVIGATIONS = NAVIGATIONS;
  readonly POWER_BI_METRICS_REPORT = environment.powerBIMetricsReport;
  powerBiMetricsReportUrl: SafeResourceUrl;

  dateRange = new UntypedFormGroup({
    start: new UntypedFormControl(StatisticsComponent.oneMonthBefore(new Date())),
    end: new UntypedFormControl(StatisticsComponent.daysBefore(new Date(), 1)),
  });
  topFilesControl = new UntypedFormControl(10);
  topCountriesControl = new UntypedFormControl(10);
  from: string = StatisticsComponent.formatDate(this.dateRange.value.start);
  to: string = StatisticsComponent.formatDate(this.dateRange.value.end);

  private subscriptions = new Subscription();

  userCount: number = 0;
  averageQueriesPerUser: number = 0;
  avgResponseTimeSec: number = 0;
  topFiles: { key: string, value: number }[] = [];
  topCountries: { countryCode: string, country: string, totalQueries: number, latitude: number, longitude: number }[] = [];
  fileCount: number = 0;

  constructor(private statisticsService: StatisticsService, private sanitizer: DomSanitizer) { }

  static formatDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  static daysBefore(date: Date, days: number): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() - days);
  }

  // Same rule as Moment: if the previous month is shorter, clamp to its last day
  static oneMonthBefore(date: Date): Date {
    const lastDayOfPreviousMonth = new Date(date.getFullYear(), date.getMonth(), 0).getDate();
    return new Date(date.getFullYear(), date.getMonth() - 1, Math.min(date.getDate(), lastDayOfPreviousMonth));
  }

  ngOnInit() {
    this.powerBiMetricsReportUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.POWER_BI_METRICS_REPORT);

    this.subscriptions.add(this.dateRange.valueChanges.subscribe(({ start, end }) => {
      if (!start || !end) return;
      this.from = StatisticsComponent.formatDate(start);
      this.to = StatisticsComponent.formatDate(end);
      this.getStatistics();
    }));

    this.subscriptions.add(this.topFilesControl.valueChanges.subscribe(() => {
      this.getStatistics();
    }));

    this.subscriptions.add(this.topCountriesControl.valueChanges.subscribe(() => {
      this.getStatistics();
    }));

    this.getStatistics();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  synchronizeMetrics() {
    this.statisticsService.synchronizeMetrics().subscribe(() => {

    });
  }

  getStatistics() {
    this.statisticsService.getStatistics(this.from, this.to, this.topFilesControl.value, this.topCountriesControl.value).subscribe((data: any) => {
      this.userCount = data.userCount;
      this.averageQueriesPerUser = data.averageQueriesPerUser;
      this.topFiles = data.topFiles;
      this.avgResponseTimeSec = data.avgResponseTimeMs / 1000;
      this.fileCount = data.fileCount;
      this.topCountries = data.topCountries;

      this.renderWorldMap();
      this.renderTopFilesPie();
    });
  }

  renderWorldMap() {
    const $map = $('#world-map');
    $map.empty();

    const markers = (this.topCountries || [])
      .filter(c => c.latitude && c.longitude)
      .map(c => ({
        latLng: [c.latitude, c.longitude],
        name: `${c.country}: ${c.totalQueries} Prompts`,
        style: {
          fill: '#000090',
          stroke: 'rgba(255,255,255,0.7)',
          "stroke-width": 3
        }
      }));

    $('#world-map').vectorMap({
      map: 'world_mill_en',
      backgroundColor: 'transparent',
      regionStyle: {
        initial: { fill: '#babbba' },
        hover: { "fill-opacity": 0.8 }
      },
      markerStyle: {
    initial: {
      r: 10,
    },
        hover: {
          r: 12,
          stroke: 'rgba(255,255,255,0.8)',
          "stroke-width": 4
        }
      },
      markers: markers
    });
  }

  private renderPieChart(
    selector: string,
    data: { label: string; data: number }[],
    legendContainer: JQuery<HTMLElement>,
    colors: string[]
  ) {
    if (!data || data.length === 0) {
      $(selector).empty();
      legendContainer.empty();
      return;
    }

    $.plot(selector, data, {
      series: {
        pie: {
          show: true,
          highlight: { opacity: 0.1 }
        }
      },
      grid: { hoverable: true },
      legend: { container: legendContainer },
      colors: colors
    });
  }

  renderTopFilesPie() {
    const max = this.topFilesControl?.value || 10;
    const files = this.topFiles || [];
    const topN = files.slice(0, max);
    const othersCount = files.slice(max).reduce((sum, file) => sum + file.value, 0);

    const data = topN.map(file => ({
      label: file.key,
      data: file.value
    }));

    if (othersCount > 0) {
      data.push({ label: 'Others', data: othersCount });
    }

    const colors = [
      "#990000", "#009900", "#000099", "#CC0066", "#666666",
      "#003300", "#003366", "#660066", "#99CC00", "#6699CC",
      "#CC99CC", "#BABBBA"
    ];

    this.renderPieChart(
      '#widget-top-files',
      data,
      $("#widget-top-files").parent().next().find(".legend"),
      colors
    );
  }

  private themeLabel(key: string): string {
    if (key === 'D') return 'Dark';
    if (key === 'L') return 'Light';
    return key;
  }
}